/**
 * WS-7203. Carries the page a signed out visitor asked for across the login round trip.
 *
 * Someone sent a link to a page inside the application, and they are not signed in. The run block
 * correctly sends them to the login screen, but the page they asked for was thrown away at that
 * point, so they signed in and landed on the home screen with nothing to say where they had been
 * heading.
 *
 * The destination is held in sessionStorage rather than in the address, which gives the "used once
 * and then forgotten" behaviour for free. A tab keeps its own sessionStorage across the navigation
 * to login.html and back, and a tab that is abandoned takes the destination with it when it closes,
 * so a later sign in from a fresh tab lands on the home screen as normal.
 *
 * This is NOT the existing savedRoute mechanism on SessionService. That one belongs to the credits
 * round trip, driven by the save-route directive, and reusing its key would make the two flows
 * steal each other's destination.
 *
 * SECURITY. An attacker who can choose where a user is sent immediately after a genuine login has a
 * very credible phishing route, because the user has just typed their password into the real login
 * screen and has every reason to trust what comes next. Two things close that off:
 *
 *   1. The page is never taken from the stored value. It is matched against a fixed list, and the
 *      destination is rebuilt as that page plus a fragment, so the browser can only ever be sent to
 *      a page of this application on the current origin.
 *   2. The fragment is validated on the way in AND again on the way out, because sessionStorage is
 *      writable by anything running on the origin, and validating only on write would be trusting
 *      the store to hold what we put there.
 *
 * Anything that fails either check is discarded rather than repaired. A half understood destination
 * is exactly the thing not to guess at.
 */

const LOGIN_DESTINATION_KEY = 'login_destination';

const ALLOWED_PAGES = [ 'workshop.html', 'dealer.html' ];

// A route inside the application, such as #/customers/1234 or #/transaction_center?tab=jobs.
function isInternalRoute ( hash : string ) : boolean {
  if (!hash || hash.indexOf('#/') !== 0) {
    return false;
  }

  // #// is protocol relative and is the shape an open redirect takes. A backslash is the same trick
  // for browsers that normalise it to a forward slash.
  if (hash.indexOf('#//') === 0 || hash.indexOf('\\') !== -1) {
    return false;
  }

  return true;
}

// Taken from our own address rather than from anything a link can carry, then matched against the
// fixed list so an unrecognised page is simply not remembered.
function currentPage () : string {
  const name = window.location.pathname.split('/').pop() || '';

  return ALLOWED_PAGES.indexOf(name) >= 0 ? name : '';
}

export function rememberLoginDestination () : void {
  const page = currentPage();
  const hash = window.location.hash;

  if (!page || !isInternalRoute(hash)) {
    return;
  }

  try {
    sessionStorage.setItem(LOGIN_DESTINATION_KEY, JSON.stringify({ hash: hash, page: page }));
  }
  catch {
    // A browser refusing session storage just does not get the return trip. It is not worth failing
    // the redirect to the login screen over.
  }
}

// Reads and clears in one go, so a destination is used once and cannot resurface on a later sign in.
// Returns an empty string when there is nothing safe to go to, which is the caller's cue to fall
// back to the usual home screen.
export function takeLoginDestination () : string {
  let stored : any;

  try {
    stored = JSON.parse(sessionStorage.getItem(LOGIN_DESTINATION_KEY));

    sessionStorage.removeItem(LOGIN_DESTINATION_KEY);
  }
  catch {
    return '';
  }

  if (!stored || ALLOWED_PAGES.indexOf(stored.page) < 0 || !isInternalRoute(stored.hash)) {
    return '';
  }

  return stored.page + stored.hash;
}
